import React from 'react';

/**
 * Compact multi-select for installation points. Renders as a scrollable
 * card of checkboxes — better UX than a native multi-select for short lists
 * (~5-20 points per machine) and consistent with the rest of the app.
 *
 * Props:
 *   points:  Array<{ id, label, current_component?: { serial_number, name } | null }>
 *   value:   string[] — currently-selected point ids
 *   onChange: (ids: string[]) => void
 *   testIdPrefix: prefix for data-testid on each checkbox (default "ip-multi")
 *   disabled: boolean
 */
const InstallationPointMultiSelect = ({
  points = [],
  value = [],
  onChange,
  testIdPrefix = 'ip-multi',
  disabled = false,
}) => {
  if (!points.length) {
    return (
      <div className="text-xs text-slate-500 italic py-2">
        No installation points defined for this equipment. Add them from the equipment page first.
      </div>
    );
  }

  const toggle = (id) => {
    if (disabled) return;
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div
      className="border rounded-md max-h-44 overflow-y-auto bg-white divide-y"
      data-testid={`${testIdPrefix}-list`}
    >
      {points.map((p) => {
        const checked = value.includes(p.id);
        return (
          <label
            key={p.id}
            className={
              'flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ' +
              (checked ? 'bg-slate-50' : '')
            }
            data-testid={`${testIdPrefix}-row-${p.id}`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(p.id)}
              disabled={disabled}
              data-testid={`${testIdPrefix}-checkbox-${p.id}`}
            />
            <span className="flex-1 truncate">
              {p.label}
              {p.current_component && (
                <span className="text-xs text-slate-500 ml-2 font-mono">
                  · SN {p.current_component.serial_number}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
};

export default InstallationPointMultiSelect;
